import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Info, Truck, X } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatPhp } from '@features/quotations/utils/pricing';
import { FreightLocationCombobox } from './FreightLocationCombobox';
import { allocateFreight, deriveMmTruckType } from '../lib';
import { freightService } from '../services';
import {
  MM_TENWH_FLAT_PHP,
  MM_TRAILER_FLAT_PHP,
  TRAILER_MIN_BILL_OPTIONS,
  TRUCK_SPECS,
  TRUCK_TYPES,
  appliedMaxLoadCap,
  effectiveTrailerMinBill,
  resolveMinBillable,
  roundFreightTons,
  type AllocationOption,
  type FreightLocationDto,
  type TrailerMinBill,
  type TruckType,
} from '../types';

export interface FreightPatch {
  destinationId?: string | null;
  withinMetroManila?: boolean;
  truckType?: TruckType | null;
  discount?: number | null;
  tollPerTruck?: number | null;
  trailerMinBillTons?: TrailerMinBill | null;
  /** 14ykpqtw3ua — whole tons ≥ 1, or null for no cap. Sent as a JSON number (see `isAllowedMaxLoadCap`). */
  maxLoadPerTruck?: number | null;
  total?: number | null;
  allocationSummary?: string | null;
}

interface Props {
  /** Total weight from the line items in kg. The cabinet converts to tons. */
  totalWeightKg: number;
  /**
   * Whether any line item carries a length ≥ 12 m. Drives MM auto-truck-class:
   * a 12m line forces a trailer. Computed by the parent so the panel doesn't need
   * the full line-item array.
   */
  hasTwelveMeterLengths: boolean;
  /** Persisted state — sourced from the parent draft so it survives reloads. */
  destinationId: string | null;
  /** Metro Manila flat-rate flag. When true, truck class is auto-derived and per-ton inputs are ignored. */
  withinMetroManila: boolean;
  /** "trailer" or "10wh". Null on drafts that haven't opened the cabinet yet — defaults applied on first interaction. */
  truckType: TruckType | null;
  /** Per-quote discount override. Null = use the default below. */
  discount: number | null;
  /** Per-quote toll-per-truck override (PHP). Null = use the default below. */
  tollPerTruck: number | null;
  /** 14ykpqtvw4a — the saved trailer minimum (40 / 45 / 50). Null = never chosen; see `effectiveTrailerMinBill` in ../types. */
  trailerMinBillTons: TrailerMinBill | null;
  /**
   * 14ykpqtw3ua — the saved max load per truck (whole tons ≥ 1). Null = no cap. Persisted on the draft and
   * billed by the server, so the screen prices from this prop — never from the field's local string.
   */
  maxLoadPerTruck: number | null;
  /**
   * NetSuite customlist563 internal-id string. `"1"` = YES (freight waived for this
   * quotation) → the panel renders disabled with an alert; `"2"` = NO. Defaults to `"2"`.
   */
  waivedFreight: string;
  readOnly?: boolean;
  onChange: (patch: FreightPatch) => void;
}

const DEFAULT_TRUCK_TYPE: TruckType = 'trailer';
const DEFAULT_DISCOUNT = 110;
const DEFAULT_TOLL_PER_TRUCK = 1500;
const WAIVED_FREIGHT_YES = '1';
const MAX_LOAD_HINT =
  'Optional. If the destination cannot accept a fully loaded truck (access/road constraints), enter the per-truck cap here. The system uses this to compute a realistic charge.';

/**
 * Freight charge cabinet. Sales picks a destination; the system applies the single DOP
 * rate from NetSuite (customrecord_truckingrates.custrecord_dop_truckingrates) and
 * computes the billed freight charge. Logistics — how many trucks — is intentionally
 * hidden: the quotation system bills for freight, it does not plan the haul.
 */
export function FreightCalculatorPanel({
  totalWeightKg,
  hasTwelveMeterLengths,
  destinationId,
  withinMetroManila,
  truckType,
  discount,
  tollPerTruck,
  trailerMinBillTons,
  maxLoadPerTruck,
  waivedFreight,
  readOnly,
  onChange,
}: Props) {
  // 14ykpqtvw4a — tons to 4 dp (0.1 kg), the one figure the server bills on; shown at that precision so the
  // "How this is computed" rows reproduce on a calculator.
  const totalWeightTons = roundFreightTons(totalWeightKg / 1000);
  const isWaived = waivedFreight === WAIVED_FREIGHT_YES;
  // Waive overrides everything else — once waived the form is uneditable regardless of
  // the parent's readOnly. Submitted/Sent quotations also pass readOnly=true.
  const effectiveReadOnly = readOnly || isWaived;
  // MM truck class is auto-derived from weight + 12m presence. The derived value
  // becomes the rendered selection when MM is on; non-MM uses the client choice
  // (falling back to the trailer default for legacy drafts where it's still null).
  const mmDerivedTruckType: TruckType = deriveMmTruckType(hasTwelveMeterLengths, totalWeightTons);
  const effectiveTruckType: TruckType = withinMetroManila
    ? mmDerivedTruckType
    : (truckType ?? DEFAULT_TRUCK_TYPE);

  // Location combobox needs the FULL location row, not just its id, to render
  // its label + rate. The combobox fetches the list itself; here we cache the
  // most-recently-selected row so the panel can render the rate without
  // round-tripping.
  const [resolvedLocation, setResolvedLocation] = useResolvedLocation(destinationId);

  // Optional max load per truck for destinations that cannot accept a fully loaded
  // trailer (access / road constraints). Lowers physical capacity per truck so more
  // trucks are needed, which increases padding (each truck still bills at its minimum
  // floor). 14ykpqtw3ua — persisted on the input draft (`maxLoadPerTruck` prop) and
  // billed by the server through the same `resolveMaxLoad` rule; it used to be local
  // state, so the posted charge ignored it. Kept across destination / truck class /
  // Metro Manila changes until sales clears it (owner, 2026-10-20).

  // 14ykpqtvw4a — 40 / 45 / 50; the rule for an unsaved choice lives in `effectiveTrailerMinBill`.
  const trailerMinBill = effectiveTrailerMinBill(trailerMinBillTons, Boolean(destinationId));

  const effectiveDiscount = discount ?? DEFAULT_DISCOUNT;
  const effectiveToll = tollPerTruck ?? DEFAULT_TOLL_PER_TRUCK;

  // Local input state for the numeric fields. Decouples keystrokes from the parent
  // draft-store update so typing stays responsive; the parent only sees the value
  // after a short debounce. Synced back from props so external changes (clear,
  // reload, undo) still flow into the visible field.
  const [discountInput, setDiscountInput] = useState<string>(discount?.toString() ?? '');
  const [tollInput, setTollInput] = useState<string>(tollPerTruck?.toString() ?? '');
  const [maxLoadInput, setMaxLoadInput] = useState<string>(maxLoadPerTruck?.toString() ?? '');
  const discountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxLoadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setDiscountInput(discount?.toString() ?? ''); }, [discount]);
  useEffect(() => { setTollInput(tollPerTruck?.toString() ?? ''); }, [tollPerTruck]);
  useEffect(() => { setMaxLoadInput(maxLoadPerTruck?.toString() ?? ''); }, [maxLoadPerTruck]);

  const rate = resolvedLocation?.dopRate ?? 0;

  const billed: AllocationOption | null = useMemo(() => {
    if (isWaived) return null;
    if (totalWeightTons <= 0) return null;
    // MM mode doesn't need a per-ton rate; per-ton mode does.
    if (!withinMetroManila && (!resolvedLocation || rate <= 0)) return null;
    return allocateFreight({
      totalWeightTons,
      withinMetroManila,
      hasTwelveMeterLengths,
      truckType: effectiveTruckType,
      trailerMinBillTons: trailerMinBill,
      rate,
      discount: effectiveDiscount,
      tollPerTruck: effectiveToll,
      // The saved prop, not the field's string: the screen prices exactly what the server will bill.
      locationMaxLoadPerTruck: withinMetroManila ? undefined : (maxLoadPerTruck ?? undefined),
    });
  }, [
    isWaived,
    resolvedLocation,
    rate,
    totalWeightTons,
    withinMetroManila,
    hasTwelveMeterLengths,
    effectiveTruckType,
    trailerMinBill,
    effectiveDiscount,
    effectiveToll,
    maxLoadPerTruck,
  ]);

  // Bubble the billed total + summary + (in MM mode) the auto-derived truck class up to
  // the parent draft. Refs avoid a feedback loop: only fire onChange when the computed
  // values actually change.
  const lastBubbledRef = useRef<{ total: number | null; summary: string | null; truckType: TruckType | null }>({
    total: null,
    summary: null,
    truckType: null,
  });
  useEffect(() => {
    if (effectiveReadOnly) return;
    const total = billed?.cost.total ?? null;
    const summary = billed && resolvedLocation
      ? buildAllocationSummary(resolvedLocation, billed.numTrucks, effectiveTruckType)
      : null;
    // Only stamp the MM-derived class onto the draft when MM is on. In per-ton mode
    // the sales user's pick stays the source of truth.
    const truckTypeForPatch: TruckType | null = withinMetroManila ? mmDerivedTruckType : null;
    if (
      lastBubbledRef.current.total === total &&
      lastBubbledRef.current.summary === summary &&
      lastBubbledRef.current.truckType === truckTypeForPatch
    ) {
      return;
    }
    lastBubbledRef.current = { total, summary, truckType: truckTypeForPatch };
    const patch: FreightPatch = { total, allocationSummary: summary };
    if (truckTypeForPatch !== null) patch.truckType = truckTypeForPatch;
    onChange(patch);
  }, [billed, resolvedLocation, effectiveTruckType, withinMetroManila, mmDerivedTruckType, effectiveReadOnly, onChange]);

  // First-open default: stamp the trailer default onto the draft so the picker
  // reflects the value being used for billing. Skip in read-only / waived and skip
  // when a truck type is already set (preserves rehydrated state from the server).
  // Also skip when MM is on — the bubbling effect above stamps the MM-derived class
  // and the default would race-fight it.
  useEffect(() => {
    if (effectiveReadOnly) return;
    if (withinMetroManila) return;
    if (truckType != null) return;
    onChange({ truckType: DEFAULT_TRUCK_TYPE });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (totalWeightTons <= 0 && !isWaived) {
    return (
      <EmptyState
        size="compact"
        icon={Truck}
        title="Enter weighted line items to compute freight."
        description="The calculator needs total weight to compute the charge."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Waived banner — sits above every section so the reason for the disabled
          state is unambiguous. Applies on drafts AND on Submitted/Sent rows. */}
      {isWaived && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-warning"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-semibold">Freight waived for this quotation</div>
            <div className="mt-0.5 text-warning/80">
              The calculator is read-only because the customer-details form has Waived
              Freight set to Yes. The submitted Estimate will not include a FREIGHT line.
            </div>
          </div>
        </div>
      )}

      {/* ① Destination */}
      <section className="space-y-3 rounded-md border bg-card p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          ① Destination
        </h3>
        <div className="space-y-2">
          <Label htmlFor="freight-location" className="text-xs">Destination</Label>
          <FreightLocationCombobox
            id="freight-location"
            value={resolvedLocation}
            onSelect={(loc) => {
              setResolvedLocation(loc);
              const patch: FreightPatch = { destinationId: loc?.id ?? null };
              // Pin the minimum the chips are showing the moment a destination lands on a quote
              // that has none saved, so the server bills exactly what sales sees. Evaluated with
              // the destination as it was BEFORE this pick: 45 on a new quote, 40 on an old one.
              if (loc && trailerMinBillTons == null) patch.trailerMinBillTons = trailerMinBill;
              onChange(patch);
            }}
            disabled={effectiveReadOnly}
          />
        </div>

        {/* Metro Manila checkbox — sits below the destination so sales can flag the
            quote into the flat-rate path. Always visible (not conditional on the
            destination or anything else). Disabled when the form is read-only / waived. */}
        <div className="flex items-start gap-2 rounded-md border bg-muted/30 p-2">
          <Checkbox
            id="freight-mm"
            checked={withinMetroManila}
            disabled={effectiveReadOnly}
            onCheckedChange={(next) => {
              if (effectiveReadOnly) return;
              onChange({ withinMetroManila: next === true });
            }}
            className="mt-0.5"
          />
          <Label htmlFor="freight-mm" className="flex-1 cursor-pointer space-y-0.5 leading-tight">
            <span className="block text-xs font-medium">Within Metro Manila</span>
            <span className="block text-[11px] font-normal text-muted-foreground">
              Flat per-truck billing (₱{MM_TENWH_FLAT_PHP.toLocaleString()} 10wh / ₱{MM_TRAILER_FLAT_PHP.toLocaleString()} trailer). Truck class auto-derived.
            </span>
          </Label>
        </div>

        {resolvedLocation && !withinMetroManila && (
          <div className="flex items-baseline justify-between text-xs">
            <span className="text-muted-foreground">DOP rate</span>
            <span className="font-medium tabular-nums">
              {rate > 0 ? `${formatPhp(rate)} / t` : 'n/a'}
            </span>
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs">Truck type</Label>
          {withinMetroManila ? (
            // MM mode: the rule chooses the class; surface it as a read-only chip with
            // an explanation so sales understands why the system picked it.
            <div className="rounded-md border bg-muted/40 p-2 text-xs">
              <div className="font-medium">{TRUCK_SPECS[effectiveTruckType].label}</div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                Auto:{' '}
                {hasTwelveMeterLengths
                  ? '12m lengths present — trailer required'
                  : totalWeightTons < 20
                    ? '<20MT, no 12m — 10-wheeler'
                    : '≥20MT — trailer'}
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                {TRUCK_TYPES.map((tt) => {
                  const spec = TRUCK_SPECS[tt];
                  const selected = effectiveTruckType === tt;
                  const minBill = resolveMinBillable(tt, trailerMinBill);
                  // 14ykpqtw3ua — name the cap on the card only when it adds trucks for this load (owner,
                  // 2026-10-20), so the extra trucks explain themselves; the same rule the charge records.
                  const appliedCap = appliedMaxLoadCap(spec, maxLoadPerTruck, totalWeightTons);
                  return (
                    <button
                      key={tt}
                      type="button"
                      onClick={() => !effectiveReadOnly && onChange({ truckType: tt })}
                      disabled={effectiveReadOnly}
                      aria-pressed={selected}
                      className={cn(
                        'flex flex-col items-start gap-0.5 rounded-md border p-2 text-left text-xs transition',
                        selected && 'border-primary bg-primary/5 ring-1 ring-primary',
                        !selected && !effectiveReadOnly && 'hover:bg-muted',
                        effectiveReadOnly && 'cursor-not-allowed opacity-60',
                      )}
                    >
                      <span className="font-medium">{spec.label}</span>
                      <span className="text-[11px] text-muted-foreground">
                        max {appliedCap != null ? `${appliedCap}t (capped)` : `${spec.maxLoad}t`} · min bill {minBill > 0 ? `${minBill}t` : 'none'}
                      </span>
                    </button>
                  );
                })}
              </div>
              {effectiveTruckType === 'trailer' && (
                <TrailerMinBillChips
                  value={trailerMinBill}
                  disabled={effectiveReadOnly}
                  onSelect={(tons) => onChange({ trailerMinBillTons: tons })}
                />
              )}
            </>
          )}
        </div>
      </section>

      {/* ② Charge — single output number, with optional knobs and a details disclosure.
          The section renders once a destination is picked. On the per-ton path we also
          require a positive DOP rate; MM bypasses that since flat rates don't depend on
          the location's DOP column. */}
      {resolvedLocation && (withinMetroManila || rate > 0) && (
        <section className="space-y-3 rounded-md border bg-card p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            ② Freight Charge
          </h3>

          {/* Adjustable inputs — per-ton only. MM uses flat rates with no toll or
              discount; we surface a one-line notice in lieu of the input grid. */}
          {withinMetroManila ? (
            <div className="rounded-md border border-dashed bg-muted/30 p-2 text-[11px] text-muted-foreground">
              Metro Manila flat-rate billing — no discount, no toll. Per-truck rate is
              fixed by truck class.
            </div>
          ) : (
            // One TooltipProvider for the grid's icon-only controls (UI gate 5): the hint glyph and the three ✕.
            <TooltipProvider>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="discount" className="flex h-4 items-center gap-1.5 text-xs">Discount per ton</Label>
                  <div className="relative">
                    <Input
                      id="discount"
                      type="number"
                      min="0"
                      step="1"
                      value={discountInput}
                      onChange={(e) => {
                        if (effectiveReadOnly) return;
                        const raw = e.target.value;
                        setDiscountInput(raw);
                        if (discountTimerRef.current) clearTimeout(discountTimerRef.current);
                        discountTimerRef.current = setTimeout(() => {
                          onChange({ discount: raw === '' ? null : Math.max(0, Number(raw)) });
                        }, 250);
                      }}
                      placeholder={`default ${DEFAULT_DISCOUNT}`}
                      className="h-8 pr-9 text-xs"
                      disabled={effectiveReadOnly}
                    />
                    {!effectiveReadOnly && (
                      <InputClearButton
                        label="Reset discount to default"
                        onClick={() => {
                          if (discountTimerRef.current) clearTimeout(discountTimerRef.current);
                          setDiscountInput('');
                          onChange({ discount: null });
                        }}
                      />
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">default ₱{DEFAULT_DISCOUNT}</div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="toll" className="flex h-4 items-center gap-1.5 text-xs">Toll per truck</Label>
                  <div className="relative">
                    <Input
                      id="toll"
                      type="number"
                      min="0"
                      step="1"
                      value={tollInput}
                      onChange={(e) => {
                        if (effectiveReadOnly) return;
                        const raw = e.target.value;
                        setTollInput(raw);
                        if (tollTimerRef.current) clearTimeout(tollTimerRef.current);
                        tollTimerRef.current = setTimeout(() => {
                          onChange({ tollPerTruck: raw === '' ? null : Math.max(0, Number(raw)) });
                        }, 250);
                      }}
                      placeholder={`default ${DEFAULT_TOLL_PER_TRUCK}`}
                      className="h-8 pr-9 text-xs"
                      disabled={effectiveReadOnly}
                    />
                    {!effectiveReadOnly && (
                      <InputClearButton
                        label="Reset toll to default"
                        onClick={() => {
                          if (tollTimerRef.current) clearTimeout(tollTimerRef.current);
                          setTollInput('');
                          onChange({ tollPerTruck: null });
                        }}
                      />
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">default ₱{DEFAULT_TOLL_PER_TRUCK}</div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="loc-max" className="flex h-4 items-center gap-1.5 text-xs">
                    Max load per truck
                    {/* Icon-only glyph — UI gate 5: shared Tooltip plus a matching aria-label. */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="cursor-help text-muted-foreground"
                          tabIndex={0}
                          aria-label={MAX_LOAD_HINT}
                        >
                          <Info className="h-3 w-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-xs">{MAX_LOAD_HINT}</p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <div className="relative">
                    {/* 14ykpqtw3ua — whole tons only, the rule the server enforces (isAllowedMaxLoadCap): a
                        decimal cap can count a different number of trucks here than on the server. A text input
                        with a digit filter, because type="number" lets "45.5" through whatever its step says.
                        Three digits is ample (a truck carries at most 50 t, and a higher cap has no effect) and
                        keeps a long paste from overflowing the server's decimal, which would blank the draft. */}
                    <Input
                      id="loc-max"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="off"
                      value={maxLoadInput}
                      onChange={(e) => {
                        if (effectiveReadOnly) return;
                        const digits = e.target.value.replace(/\D/g, '').replace(/^0+/, '').slice(0, 3);
                        setMaxLoadInput(digits);
                        if (maxLoadTimerRef.current) clearTimeout(maxLoadTimerRef.current);
                        maxLoadTimerRef.current = setTimeout(() => {
                          onChange({ maxLoadPerTruck: digits === '' ? null : Number(digits) });
                        }, 250);
                      }}
                      placeholder="No cap"
                      className="h-8 pr-9 text-xs"
                      disabled={effectiveReadOnly}
                    />
                    {!effectiveReadOnly && (
                      <InputClearButton
                        label="Clear max load per truck"
                        onClick={() => {
                          if (maxLoadTimerRef.current) clearTimeout(maxLoadTimerRef.current);
                          setMaxLoadInput('');
                          onChange({ maxLoadPerTruck: null });
                        }}
                      />
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">optional, whole tons</div>
                </div>
              </div>
            </TooltipProvider>
          )}

          {/* Total weight readout */}
          <div className="flex items-baseline justify-between text-xs">
            <span className="text-muted-foreground">Total weight</span>
            <span className="font-medium">{totalWeightTons.toFixed(4)} t</span>
          </div>

          {/* The big number — billed (rounded up to nearest ₱1,000). DOP/MM rates are
              already tax-inclusive so this IS the customer total; do NOT multiply by 1.12. */}
          {billed ? (
            <div className="rounded-md border border-success/30 bg-success/10 p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs text-muted-foreground">Freight charge</span>
                <span className="text-2xl font-semibold tabular-nums">
                  {formatPhp(billed.cost.billedTotal)}
                </span>
              </div>
              {/* Audit row — visible only when round-up actually moved the number. Round-up
                  applies to PER_TON only (e.g. ₱45,300 → billed ₱46,000), so this row only
                  surfaces for PER_TON when the gap is non-zero. MM flat totals pass through
                  unchanged and hide the row. */}
              {billed.cost.billedTotal !== billed.cost.total && (
                <div
                  className="mt-1.5 flex items-baseline justify-between gap-2 border-t border-success/20 pt-1.5 text-xs"
                  title="Sales rounds up to the nearest ₱1,000 for invoicing — billed minus computed is the round-up markup"
                >
                  <span className="text-muted-foreground">Computed (pre round-up)</span>
                  <span className="tabular-nums">{formatPhp(billed.cost.total)}</span>
                </div>
              )}
              <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
                <span>effective rate</span>
                <span>{formatPhp(billed.cost.effectiveRatePerTon)} / t</span>
              </div>
            </div>
          ) : (
            <EmptyState size="compact" title="Cannot compute a charge with the current inputs." />
          )}

          {/* Details disclosure — collapsed by default. Per-ton mode shows the
              delivered + padding + toll decomposition; MM mode shows the flat-rate
              math (numTrucks × flatRatePerTruck). */}
          {billed && (
            <details className="group rounded border bg-muted/30 px-2.5 py-1.5 text-xs">
              <summary className="cursor-pointer select-none text-muted-foreground hover:text-foreground">
                How this is computed
              </summary>
              <div className="mt-2 space-y-1.5">
                {billed.billingMode === 'MM_FLAT' ? (
                  <Row
                    label={`Metro Manila · ${billed.numTrucks} ${TRUCK_SPECS[billed.truckType].label.toLowerCase()}${billed.numTrucks === 1 ? '' : 's'} × ${formatPhp(billed.flatRatePerTruck ?? 0)}`}
                    value={billed.cost.mmFlatCost}
                  />
                ) : (
                  <>
                    <Row
                      label={`Delivered ${billed.delivered.toFixed(4)}t × ${formatPhp(rate - effectiveDiscount)} (${formatPhp(rate)} − ${formatPhp(effectiveDiscount)} discount)`}
                      value={billed.cost.deliveredCost}
                    />
                    {/* Names the floor so a jump in price explains itself (14ykpqtvw4a). */}
                    <Row
                      label={
                        billed.padding > 0
                          ? `Padding ${billed.padding.toFixed(4)}t × ${formatPhp(rate)} (${billed.numTrucks} × ${billed.minBillPerTruck}t min bill − ${billed.delivered.toFixed(4)}t; no discount)`
                          : `Padding 0.0000t (${billed.numTrucks} × ${billed.minBillPerTruck}t min bill ≤ ${billed.delivered.toFixed(4)}t delivered)`
                      }
                      value={billed.cost.paddingCost}
                    />
                    {/* 14ykpqtw3ua — names the cap when it set the truck count, so the extra trucks explain themselves. */}
                    <Row
                      label={`Toll fees · ${billed.numTrucks} truck${billed.numTrucks === 1 ? '' : 's'}${billed.maxLoadPerTruck != null ? ` (max ${billed.maxLoadPerTruck} t each)` : ''} × ${formatPhp(effectiveToll)}`}
                      value={billed.cost.tollCost}
                    />
                  </>
                )}
                <div className="border-t pt-1.5">
                  <Row label="TOTAL" value={billed.cost.total} bold />
                </div>
              </div>
            </details>
          )}
        </section>
      )}
    </div>
  );
}

function buildAllocationSummary(loc: FreightLocationDto, numTrucks: number, truckType: TruckType): string {
  const place = loc.cityName + (loc.name ? ` (${loc.name})` : '');
  const label = TRUCK_SPECS[truckType].label.toLowerCase();
  return `${numTrucks} ${label}${numTrucks === 1 ? '' : 's'} to ${place}`;
}

/**
 * 14ykpqtvw4a — the trailer's minimum billable tons, one of three presets. Lives directly under the
 * truck cards, left-aligned so it reads as belonging to the Trailer card above it: the minimum is a
 * property of the trailer (its caption already says "min bill"), not a typed knob like Discount / Toll
 * in section ②. Selecting a chip re-prices instantly — the charge is computed client-side.
 */
function TrailerMinBillChips({
  value,
  disabled,
  onSelect,
}: {
  value: TrailerMinBill;
  disabled: boolean;
  onSelect: (tons: TrailerMinBill) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-1">
      <span className="text-xs font-medium">Trailer min bill</span>
      <div role="group" aria-label="Trailer minimum billable tonnage" className="flex gap-1.5">
        {TRAILER_MIN_BILL_OPTIONS.map((tons) => {
          const selected = value === tons;
          return (
            <button
              key={tons}
              type="button"
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => !selected && onSelect(tons)}
              className={cn(
                'h-7 rounded-md border px-3 text-xs tabular-nums transition',
                // primary-ink, not primary: brand red on its own 10% tint is ~3.2:1 as 12px text.
                selected
                  ? 'border-primary bg-primary/10 font-semibold text-primary-ink'
                  : 'text-muted-foreground',
                !selected && !disabled && 'hover:bg-muted hover:text-foreground',
                disabled && 'cursor-not-allowed opacity-60',
              )}
            >
              {tons} t
            </button>
          );
        })}
      </div>
      <span className="text-xs text-muted-foreground">billed per trailer, even when it carries less</span>
    </div>
  );
}

/**
 * The ✕ inside a numeric field (reset to default / clear). Icon-only, so UI gate 5: the shared Tooltip plus a
 * `title` and `aria-label` in the same words. Needs a TooltipProvider above it — the input grid has one.
 */
function InputClearButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className="absolute right-0 top-0 flex h-8 w-8 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          title={label}
          aria-label={label}
        >
          <X className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function Row({ label, value, bold }: { label: string; value: number; bold?: boolean }) {
  return (
    <div className={cn('flex items-baseline justify-between gap-2', bold && 'text-sm font-semibold')}>
      <span className={cn(!bold && 'text-muted-foreground')}>{label}</span>
      <span className="font-mono tabular-nums">{formatPhp(value)}</span>
    </div>
  );
}

// --- Local hooks --------------------------------------------------------------

/** Resolves a location id back to a full row by calling /locations/{id} once. */
function useResolvedLocation(destinationId: string | null) {
  const [loc, setLoc] = useState<FreightLocationDto | null>(null);
  useEffect(() => {
    if (!destinationId) {
      setLoc(null);
      return;
    }
    if (loc?.id === destinationId) return;
    let cancelled = false;
    freightService.getLocationById(destinationId).then(
      (resolved) => { if (!cancelled) setLoc(resolved); },
      () => { if (!cancelled) setLoc(null); },
    );
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationId]);
  return [loc, setLoc] as const;
}
